/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { useCallback, useEffect, useLayoutEffect, useMemo } from 'react';
import { v4 as uuidV4 } from 'uuid';

import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import {
  DocumentVersionState,
  DocumentVersionType,
  FhirPredefinedWidget,
  FhirPageWidgetType,
  PageFhirType,
  PageType,
  HtmlFhirInstance,
  HtmlFhirInstanceGrouped,
  HtmlFhirInstanceUngrouped,
} from '../algorithm/types';
import { useMessageIndexIfExists, usePageIfExists } from '../chat/hooks/MessageContext';
import {
  removeListOfHtmlFhir,
  selectListOfHtmlFhirById,
  selectPredefinedWidgetsMap,
  setListOfHtmlFhir,
} from './fhirSlice';
import { defaultPageFhir, getFhirPredefinedWidgetForFhirPageWidget, resolveFhirPredefinedWidgetId } from './fhir_utils';

/**
 * This hook is used to update the list of fhir widgets in the sidebar, saving the list of fhir widgets
 * in the html, to the store `state.fhir.listOfHtmlFhirById`
 * It returns a function that can be called to update, when the content of the page changes other than
 * value updated. Currently only used for when embedded documents load.
 *
 * This is update is also used when we have an embedded document, and that embeddded document is loaded.
 */
export const useUpdateFhirSidebar = (
  ref: React.RefObject<HTMLDivElement>,
  value: string,
  fhir: PageFhirType | undefined,
  pageId: string | undefined,
  documentVersionId: string | undefined,
  inEditor = false
) => {
  const id = useMemo(() => uuidV4(), []);
  const dispatch = useAppDispatch();
  const messageIndex = useMessageIndexIfExists();
  useEffect(() => {
    // Note this is a cleanup function, so it runs when the component unmounts
    // We remove the list of fhir widgets so they aren't shown in the sidebar
    return () => {
      dispatch(removeListOfHtmlFhir({ id }));
    };
  }, [dispatch, id]);
  /* Updates the what fhir items we have in the sidebar, and their position.
   * This is called when the content of the page changes, or when an embedded document is loaded.
   * It will look for all <fhir> tags in the content, and update the store state with the list of fhir items.
   */
  const doUpdateFhirSidebar = useCallback(() => {
    const html = ref?.current;
    if (!html) {
      dispatch(removeListOfHtmlFhir({ id }));
      return;
    } else {
      // console.log('useUpdateFhirSidebar', html, { id });
    }
    const seen = new Set<string>();
    const listOfHtmlFhir = [] as HtmlFhirInstance[];
    html?.querySelectorAll('fhir').forEach((node, index) => {
      const id = node.getAttribute('id') || index.toString(); // we should never need to use index, that's just as a fallback
      const htmlDataPredefinedFhirWidgetIds = node
        .getAttribute('data-fhir-widget-id')
        ?.split(';')
        .map((x) => x.trim())
        .filter(Boolean);
      // These are set when inside an <embeddocumentpage> tag, and indicate an embedded page from another document
      const embedded_doc_id = node.getAttribute('data-doc-id');
      const embedded_page_id = node.getAttribute('data-page-id'); // For embedded documents, this is the page id of the embedded document page
      const seen_id = embedded_doc_id ? `${embedded_doc_id}-${id}` : id; // id is just used for uniqueness, but it might not be unique for different doc_id's
      if (seen.has(seen_id)) {
        return;
      }
      seen.add(seen_id);
      let n = node as HTMLElement;
      while (n.offsetTop === 0 && n.offsetHeight === 0 && n.parentElement) {
        n = n.parentElement;
      }
      const y = n.offsetTop;
      const page_id = embedded_page_id || pageId;
      if (!page_id) {
        console.error('contextualized fhir tag used but no pageId nor any data-page-id was supplied');
        return;
      }
      listOfHtmlFhir.push({
        id,
        y,
        inEditor,
        embedded_doc_id: embedded_doc_id ? embedded_doc_id : undefined,
        pageId: embedded_doc_id ? embedded_page_id || '' : page_id,
        messageIndex: Math.floor(messageIndex || 0),
        contextualized_text: node.textContent || '',
        htmlDataPredefinedFhirWidgetIds: htmlDataPredefinedFhirWidgetIds?.length
          ? htmlDataPredefinedFhirWidgetIds
          : undefined,
        documentVersionId,
      });
    });
    // Note this will check if the list of fhir components has changed, and if so, update the state
    // if (JSON.stringify(state.listOfHtmlFhir[id]) !== JSON.stringify(listOfHtmlFhir))

    /* The id is a random id */
    dispatch(setListOfHtmlFhir({ id, listOfHtmlFhir }));
    return listOfHtmlFhir.length > 0;
  }, [ref, dispatch, id, pageId, messageIndex, documentVersionId, inEditor]);

  useLayoutEffect(() => {
    const numFound = doUpdateFhirSidebar();
    let retryTimeout: ReturnType<typeof setTimeout> | undefined;
    if (!numFound) {
      // Might be that there's just no contextualization, but it might be
      // That the ckeditor just hasn't loaded yet.
      // Call again on a small timeout to ensure the dom has been updated
      retryTimeout = setTimeout(doUpdateFhirSidebar, 100);
    }
    return () => clearTimeout(retryTimeout);
  }, [doUpdateFhirSidebar, fhir, value]);
  return { doUpdateFhirSidebar };
};

/** This adds a single button to listOfHtmlFhir so it can shown in the fhir sidebar, so we can contextualize buttons */
export const useUpdateButtonFhirSidebar = (
  buttonRef: React.RefObject<HTMLElement>,
  fhir: FhirPageWidgetType | undefined,
  pageId: string | undefined,
  fhirWidgetId: string | undefined,
  documentVersionId: string | undefined,
  buttonText: string | undefined,
  inEditor = false
) => {
  const id = useMemo(() => uuidV4(), []);
  const dispatch = useAppDispatch();
  const messageIndex = useMessageIndexIfExists();
  useEffect(() => {
    // Note this is a cleanup function, so it runs when the component unmounts
    // We remove the list of fhir widgets so they aren't shown in the sidebar
    return () => {
      id && dispatch(removeListOfHtmlFhir({ id }));
    };
  }, [dispatch, id]);
  /* Updates the what fhir items we have in the sidebar, and their position.
   * This is called when the content of the page changes, or when an embedded document is loaded.
   * It will look for all <fhir> tags in the content, and update the store state with the list of fhir items.
   */
  const doUpdateFhirSidebar = useCallback(() => {
    if (!fhirWidgetId) return;
    if (!id) {
      console.warn('useUpdateFhirSidebar - No id supplied');
      return;
    }
    const button = buttonRef?.current;
    if (!button) {
      console.log('useUpdateFhirSidebar - No ref to button element');
      dispatch(removeListOfHtmlFhir({ id }));
      return;
    } else {
      // console.log('useUpdateFhirSidebar', button, { id });
    }
    if (pageId == null) {
      console.error('contextualized fhir tag used but no pageId was supplied');
      return;
    }
    let n = button as HTMLElement;
    while (n.offsetTop === 0 && n.offsetHeight === 0 && n.parentElement) {
      n = n.parentElement;
    }
    const y = n.offsetTop;

    // These are set when inside an <embeddocumentpage> tag, and indicate an embedded page from another document
    const embedded_doc_id = button.getAttribute('data-doc-id');
    const embedded_page_id = button.getAttribute('data-page-id');

    const listOfHtmlFhir: HtmlFhirInstance[] = [
      {
        id: fhirWidgetId,
        y,
        inEditor,
        embedded_doc_id: embedded_doc_id ? embedded_doc_id : undefined,
        pageId: embedded_page_id ? embedded_page_id : pageId,
        messageIndex: Math.floor(messageIndex || 0),
        contextualized_text: button.textContent || buttonText || '',
        isButton: true,
        documentVersionId: documentVersionId,
      },
    ];
    // Note this will check if the list of fhir components has changed, and if so, update the state
    // if (JSON.stringify(state.listOfHtmlFhir[id]) !== JSON.stringify(listOfHtmlFhir))

    /* The id is a random id */
    dispatch(setListOfHtmlFhir({ id, listOfHtmlFhir }));
    return listOfHtmlFhir.length > 0;
  }, [fhirWidgetId, id, buttonRef, pageId, messageIndex, buttonText, documentVersionId, dispatch, inEditor]);

  useLayoutEffect(() => {
    doUpdateFhirSidebar();
  }, [doUpdateFhirSidebar, fhir]);
  return { doUpdateFhirSidebar };
};

/**
 * For each htmlFhir, add the predefinedWidget it points to, based on the current page context which holds the widget information.
 *
 * combinedFhirConfigWithSamePrefinedWidget will group these.
 */
export const mapHtmlFhirInstanceToHtmlFhirInstanceUngrouped_ = (
  htmlFhir: HtmlFhirInstance[],
  page_: PageType | undefined,
  publishedDocuments: {
    [documentId: string]: (DocumentVersionType & { state: DocumentVersionState.PUBLISHED }) | null;
  },
  documentVersions: Record<string, { pages: Record<string, PageType> }>,
  predefinedWidgetOptionsMap: { [id: string]: FhirPredefinedWidget } | undefined,
  unsavedPageChanges: {
    [id: string]: PageType;
  }
): HtmlFhirInstanceUngrouped[] => {
  const ret: HtmlFhirInstanceUngrouped[] = htmlFhir.flatMap((fhir) => {
    let page: PageType | undefined;
    if (fhir.embedded_doc_id) {
      page = publishedDocuments[fhir.embedded_doc_id]?.pages[fhir.pageId];
    } else if (page_?.id === fhir.pageId) {
      page = page_;
      // console.log('useHtmlFhirForMessage - using page context', page);
    } else {
      page = unsavedPageChanges[fhir.pageId] || documentVersions[fhir.documentVersionId!]?.pages[fhir.pageId];
      // console.log('useHtmlFhirForMessage - using unmodified page', page);
    }
    const pageFhir = page?.fhir || defaultPageFhir;
    const fhirWidget = pageFhir.idsToFhir[fhir.id];
    const predefinedWidgets: FhirPageWidgetType['predefinedWidgets'] = fhirWidget
      ? fhirWidget.predefinedWidgets
      : fhir.htmlDataPredefinedFhirWidgetIds?.map((id) => ({ id, observationGroup: undefined })) || [];
    const htmlFhirInstances: HtmlFhirInstanceUngrouped[] = predefinedWidgets.length
      ? predefinedWidgets.map((pw) => {
          const predefinedWidgetId = resolveFhirPredefinedWidgetId(pw?.id, predefinedWidgetOptionsMap);
          const fhirConfig = getFhirPredefinedWidgetForFhirPageWidget(predefinedWidgetId, predefinedWidgetOptionsMap);
          return {
            ...fhir,
            type: fhirConfig?.type || 'Labs',
            predefinedWidgetId,
            predefinedWidget: fhirConfig,
            observationGroupId: pw.observationGroup?.id,
          };
        })
      : [
          // No predefined widget, so just show the unconfigured fhir
          {
            ...fhir,
            type: 'Labs' as const,
            predefinedWidgetId: undefined,
            predefinedWidget: undefined,
            observationGroupId: undefined,
          },
        ];
    return htmlFhirInstances;
  });
  // console.log('useHtmlFhirForMessage', { htmlFhir, ret });
  return ret;
};

/**
 * If we have, say, two <fhir> tags (which will always have unique ids), but they point to the same predefined widget,
 * we should combine them into one widget in the sidebar.
 * This is because the predefined widget will be the same, and we don't want to show the same widget twice.
 *
 * Exported only for testing.
 */
export function combinedFhirConfigWithSamePrefinedWidget_(
  htmlFhir: HtmlFhirInstanceUngrouped[],
  combineSamePredefinedWidget: boolean
): HtmlFhirInstanceGrouped[] {
  const fhirMap = new Map<string, HtmlFhirInstanceGrouped>();

  function ungroupedToGrouped(fhir: HtmlFhirInstanceUngrouped): HtmlFhirInstanceGrouped {
    const { observationGroupId, isButton, ...rest } = fhir; // TODO Remove id as well
    return {
      ...rest,
      fhirWidgetIds: [fhir.id],
      observationGroupIds: fhir.observationGroupId ? [fhir.observationGroupId] : [],
    };
  }

  return htmlFhir.reduce((acc, fhir) => {
    if (!fhir.predefinedWidgetId || !combineSamePredefinedWidget) {
      acc.push(ungroupedToGrouped(fhir));
    } else if (!fhirMap.has(fhir.predefinedWidgetId)) {
      const fhir_ = ungroupedToGrouped(fhir);
      acc.push(fhir_);
      fhirMap.set(fhir.predefinedWidgetId, fhir_);
    } else {
      const existing = fhirMap.get(fhir.predefinedWidgetId)!;
      existing.fhirWidgetIds.push(fhir.id);
      if (fhir.observationGroupId) existing.observationGroupIds.push(fhir.observationGroupId);
    }
    return acc;
  }, [] as HtmlFhirInstanceGrouped[]);
}

/** For the given message index:
 *
 * - Get the list of <fhir> tags in the current page and the buttons with the fhir config. These will point to a particular widgetId.
 * - Look at the page info to map the widgetId to the fhir config, to get the predefined widget id these point to
 *
 * Note, the usePageIfExists() isn't really that useful here, because useHtmlFhirForMessage() is used
 * at a message level, and the page context is really only set for the page inside.
 * So this page_ could be a TABS page etc.
 */
export function useHtmlFhirForMessage(
  messageIndex: number,
  combineSamePredefinedWidget: boolean,
  inEditorMode = false
) {
  /* htmlFhir is the list of <fhir> tags we found in the current page.
     It's set by setListOfHtmlFhir in useUpdateFhirSidebar() in
     RenderedHTML or by Editor.tsx */
  const htmlFhir_ = useAppSelector(selectListOfHtmlFhirById);
  const page_ = usePageIfExists(); // See comment above
  const publishedDocuments = useAppSelector((state) => state.chat.publishedDocuments);
  const documentVersions = useAppSelector((state) => state.chat.documentVersions);
  const predefinedWidgetOptionsMap = useAppSelector(selectPredefinedWidgetsMap);
  const unsavedPageChanges = useAppSelector((state) => state.chat.unsavedPages);

  // fhir disclaimer needs to displayed at the top of sidebar behind the if feature flag in OFF
  const firstFhirMessageIndex = useMemo(() => {
    return Math.min(...htmlFhir_.map((x) => Math.floor(x.messageIndex)));
  }, [htmlFhir_]);

  const { htmlFhirConfig, htmlFhirConfigUngrouped, validFhirExists } = useMemo(() => {
    const relevantHtmlFhir = htmlFhir_.filter((f) => !!f.inEditor === inEditorMode);
    const allHTMLFhirConfig_ = mapHtmlFhirInstanceToHtmlFhirInstanceUngrouped_(
      relevantHtmlFhir,
      page_?.page,
      publishedDocuments,
      documentVersions,
      predefinedWidgetOptionsMap,
      unsavedPageChanges
    );
    function isUnconfiguredButton(f: HtmlFhirInstanceUngrouped) {
      return !!f?.isButton && !f.predefinedWidgetId;
    }
    const validFhirExists = allHTMLFhirConfig_.some((f) => !isUnconfiguredButton(f)); // Don't count unconfigured buttons
    const htmlFhirConfigUngrouped = allHTMLFhirConfig_.filter((f) => Math.floor(f.messageIndex) === messageIndex);
    const htmlFhirConfig = combinedFhirConfigWithSamePrefinedWidget_(
      htmlFhirConfigUngrouped.filter((f) => !isUnconfiguredButton(f)),
      combineSamePredefinedWidget
    );
    return { htmlFhirConfig, htmlFhirConfigUngrouped, validFhirExists };
  }, [
    htmlFhir_,
    page_?.page,
    publishedDocuments,
    documentVersions,
    predefinedWidgetOptionsMap,
    unsavedPageChanges,
    combineSamePredefinedWidget,
    inEditorMode,
    messageIndex,
  ]);
  return { htmlFhirConfig, htmlFhirConfigUngrouped, validFhirExists, firstFhirMessageIndex };
}
